import {api, RequestParam} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {TimeAccountBase, TimeAccountDashboardConfig} from "./timeAccountBase";
import {TimeAccountTable} from "@/features/timeaccounts/timeAccountTable";
import {CorrectWorkBalanceDialog} from "@/features/timeaccounts/correctWorkBalanceDialog";
import {TimeAccountIntegrationExportDialog} from "@/features/timeaccounts/timeAccountIntegrationExportDialog";
import {TimeAccountInitializationDialog} from "@/features/timeaccounts/timeAccountInitializationDialog";
import {UserChangeConfigurationDialogHandler} from "@/features/user/userChangeConfigurationDialog";
import {ReportingQueryParams, TimeAccountDashboardQueryParams} from "@/shared/queryParams";
import {GroupDropdown} from "@/shared/ui/groupDropdown";
import {Combobox} from "@/shared/ui/combobox";
import {buildRecordingValidationContainerContent} from "@/shared/validations/recordingValidationUtil";
import * as TimeVacationAccountInfo from "@/features/timeaccounts/timeVacationAccountInfo";
import {htmlEscape} from "@/shared/utils/string";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	formatDate,
	formatDuration,
	formatMonth,
	formatTime,
	formatVacation,
	getCurrentISOOffset,
	getISODate,
	isSameOrAfterDay,
	isSameOrBeforeDay,
	parseDateInput,
	parseISODate,
	parseTimeInput,
	parseVacation,
	Temporal
} from "@/shared/utils/datefunctions";
import {
	ErrorResponse,
	Group,
	HolidayCalendar,
	IntegrationDetails,
	TimeAccountDashboardBalanceMode,
	TimeAccountExitBookingBalanceData,
	TimeAccountTableRow,
	TimeAccountVacationData,
	User,
	WorkingTimeRange,
	WorkScheduleModel
} from "@/shared/timr";
import {
	ExportTimeAccountType,
	ExportType,
	TimeAccountEntryType,
	TimeAccountState,
	TimeNavigationType,
	VacationUnit
} from "@/shared/timrTypes";
import {promiseNow} from "@/shared/utils/promise";

interface GetResignedUsersForBalanceResponse {
	users: User[]
}

interface TimeAccountBalanceBookingResponse {
	timeAccountIds: number[];
}

interface TimeAccountExportIntegrationDetails extends IntegrationDetails {
	configuration?: {
		timeAccountAbsenceExportEnabled?: boolean;
		timeAccountPayrollExportEnabled?: boolean;
	};
}

export class TimeAccountDashboardPage extends TimeAccountBase {

	public static readonly GROUP_TIME_ACCOUNT = "timeAccountGroup";

	private memberCountForCurrentGroupSelection: number | null = null;
	private usersWithInitializedTimeAccount: User[];

	private createTimeAccountBalanceDialog: BootstrapModal.Modal;
	private correctVacationBalanceDialog: BootstrapModal.Modal;
	private exportTimeAccountDialog: BootstrapModal.Modal;
	private createExitBookingDialog: BootstrapModal.Modal;
	private correctWorkBalanceDialog: CorrectWorkBalanceDialog;
	private timeAccountInitializationDialog: TimeAccountInitializationDialog;
	private userChangeConfigurationDialogHandler: UserChangeConfigurationDialogHandler;

	private exportPayoffsExcel: boolean;
	private exportBalances: boolean;

	private groupDropdown: GroupDropdown;
	private createTimeAccountBalanceUserGroupDropdown: GroupDropdown;
	private exportTimeAccountBalanceGroupDropdown: GroupDropdown;

	private userCombobox: Combobox<User>;
	private correctVacationBalanceUserCombobox: Combobox<User>;
	private createTimeAccountBalanceUserCombobox: Combobox<User>;
	private exportTimeAccountBalanceUserCombobox: Combobox<User>;

	private timeAccountEntryType: TimeAccountEntryType;
	private exportExcel: boolean;
	private balanceDates: Temporal.PlainDate[] = [];

	private loadExitBookingBalanceDataRequest: JQuery.jqXHR;
	private loadFutureWorkingTimesAfterResignDateRequest: JQuery.jqXHR;

	private workScheduleModels: WorkScheduleModel[];
	private holidayCalendars: HolidayCalendar[];

	constructor(config: TimeAccountDashboardConfig) {
		config.timeAccountViewType = "ALL_ACCOUNTS";
		super(config);

		this.exportBalances = true;
		this.exportPayoffsExcel = true;

		this.exportExcel = true;
		this.usersWithInitializedTimeAccount = config.usersWithInitializedTimeAccount;
		this.correctWorkBalanceDialog = new CorrectWorkBalanceDialog({
			usersWithInitializedTimeAccount: config.usersWithInitializedTimeAccount,
			defaultOvertimeRules: config.overtimeRules
		});
		$(this.correctWorkBalanceDialog).on(CorrectWorkBalanceDialog.EVENT_TIME_ACCOUNT_CORRECTED, () => {
			this.timeAccountTable.reload();
			this.reloadCurrentTimeAccountData();
		});

		this.timeAccountEntryType = TimeAccountDashboardQueryParams.get("actionFilter")
			?? BrowserStorageSettings.getTimeAccountActionFilter(TimeAccountDashboardPage.GROUP_TIME_ACCOUNT);

		const actionFilter = $("#timeAccountFilterAction");
		actionFilter.val(this.timeAccountEntryType);
		actionFilter.on("change", e => {
			this.timeAccountEntryType = $(e.target).val() as TimeAccountEntryType;
			BrowserStorageSettings.setTimeAccountActionFilter(TimeAccountDashboardPage.GROUP_TIME_ACCOUNT, this.timeAccountEntryType);
			this.reloadCurrentTimeAccountData();
		});

		const defaultUserId = TimeAccountDashboardQueryParams.get("userFilter")
			?? BrowserStorageSettings.getTimeAccountDashboardUserFilter(TimeAccountDashboardPage.GROUP_TIME_ACCOUNT);
		const defaultShowInactiveUsersValue = TimeAccountDashboardQueryParams.get("showInactiveUsers") ?? false;
		$("#timeAccountDashboardShowInactive").prop("checked", defaultShowInactiveUsersValue);

		this.userCombobox = new Combobox({
			items: config.users,
			formatter: user => {
				return user.fullname;
			},
			idElement: $("#timeAccountDashboardFilterUserId"),
			filterElement: $("#timeAccountDashboardFilterUserFilter"),
			caretElement: $("#timeAccountDashboardFilterUserCaret"),
			emptyMessage: messages["user.filter.all"],
			clearEntryMessage: messages["user.filter.all"]
		});
		if (defaultShowInactiveUsersValue) {
			this.filterUsersCombobox(this.userCombobox, this.groupDropdown?.getSelectedId(), defaultShowInactiveUsersValue, defaultUserId);
		}
		this.userCombobox.setSelectedItemById(defaultUserId, config.users.length === 1);
		$(this.userCombobox).on(Combobox.EVENT_CHANGED, () => {
			const userId = this.userCombobox.getSelectedItem()?.id;
			BrowserStorageSettings.setTimeAccountUserFilter(TimeAccountDashboardPage.GROUP_TIME_ACCOUNT, userId);
			this.onSelectedUserChanged();
		});
		this.groupDropdown = new GroupDropdown({
			rootElement: $("#timeAccountDashboardFilterGroupDropdown"),
			allowSelectionOfNotAssigned: false,
			emptyMessage: messages["group.filter.all"],
			clearEntryMessage: messages["user.filter.all"]
		});
		const defaultGroup = BrowserStorageSettings.getTimeAccountGroupFilter(TimeAccountDashboardPage.GROUP_TIME_ACCOUNT);
		if (defaultGroup) {
			this.groupDropdown.setSelection(defaultGroup.id, defaultGroup.name, defaultGroup.breadCrumbs, defaultGroup);
		}
		this.filterUsersCombobox(this.userCombobox, defaultGroup?.id);
		$(this.groupDropdown).on(GroupDropdown.EVENT_SELECTED, (e, group: Group) => {
			BrowserStorageSettings.setTimeAccountGroupFilter(TimeAccountDashboardPage.GROUP_TIME_ACCOUNT, group);
			this.timeAccountTable.filterByGroupAndUserAndAction(group?.id, this.userCombobox.getSelectedItem()?.id, this.timeAccountEntryType);
			if (globals.validateWorkingTime || globals.validateWorkingTimeOverlaps) {
				this.validateWorkingTimes(group?.id, this.userCombobox.getSelectedItem()?.id, true);
			}
			this.filterUsersCombobox(this.userCombobox, group?.id);
		});
		const selectedUser = this.userCombobox.getSelectedItem();
		if (this.shouldValidateWorkingTimes(selectedUser)) {
			this.validateWorkingTimes(this.groupDropdown.getSelectedId(), selectedUser?.id, true);
		}
		this.toggleUserLicenceOnExitInfo(selectedUser);
		this.timeAccountTable = new TimeAccountTable({
			element: $("#timeAccountTable"),
			initialFilter: TimeAccountTable.buildFilterParams(
				this.groupDropdown.getSelectedId(),
				selectedUser?.id,
				this.timeAccountEntryType
			),
			timeAccountBalanceChanged: () => {
				this.reloadTimeAccountBalanceDates();
				this.reloadTimeAccountPayoffsMonths();
				this.reloadCurrentTimeAccountData();
				this.reloadUsersWithInitializedTimeAccount(this.groupDropdown.getSelectedId(), this.getShowInactiveValue());
			},
			orderable: false,
			clampNote: false,
			allowExpand: false,
			showContextMenuDeleteButton: globals.isAdmin, // Only admins are allowed delete time account entries
			showUserNameColumn: true,
			showOvertimesColumn: config.showOvertimesColumn,
			showTravellingTimesColumn: config.showTravellingTimesColumn,
			editTimeAccountInitialization: (timeAccount) => {
				this.timeAccountInitializationDialog.showForEdit(timeAccount);
			},
			editVacationBalanceCorrection: (timeAccount) => {
				this.configureCorrectVacationBalanceUser(timeAccount);
				this.correctVacationBalanceDialog.show();
			},
			editWorkBalanceCorrection: (timeAccount) => {
				this.correctWorkBalanceDialog.showForEdit(timeAccount);
			}
		});
		this.initCreateTimeAccountBalanceDialog();
		this.initCorrectVacationBalanceDialog();
		this.initExportTimeAccountDialog();
		this.initCreateExitBookingDialog();
		this.initUserChangeConfigurationDialog();

		if (selectedUser) {
			this.toggleTimeVacationAccountInfoPanel(true);
			this.getCurrentTimeAccountVacationData(BrowserStorageSettings.getTimeAccountDashboardBalanceMode(TimeAccountBase.GROUP_TIME_ACCOUNT) || "TODAY", selectedUser.id);
		}

		$("#timeAccountDashboardShowInactive").change(() => {
			this.filterUsersCombobox(this.userCombobox, this.groupDropdown.getSelectedId());
		});

		$("#createTimeAccountBalanceButton").click(() => {
			this.showCreateTimeAccountBalanceDialog();
		});

		$("#correctWorkBalanceButton").click(() => {
			const defaultUser = this.userCombobox.getSelectedItem();
			this.correctWorkBalanceDialog.showCorrectWorkBalanceDialog(defaultUser);
		});

		$("#correctVacationBalanceButton").click(() => {
			this.showCorrectVacationBalanceDialog();
		});

		$("#exportTimeAccountButton").click(() => {
			this.showExportTimeAccountDialog();
		});
		this.initTimeAccountIntegrationExport();

		this.timeAccountInitializationDialog = new TimeAccountInitializationDialog();

		$("#initTimeAccountButton").click(() => {
			this.timeAccountInitializationDialog.showForUser(this.userCombobox.getSelectedItem(), TimeAccountEntryType.UserEntry);
		});

		$("#contextCreateReEntryBookingButton").click(() => {
			this.timeAccountInitializationDialog.showForUser(this.userCombobox.getSelectedItem(), TimeAccountEntryType.ReEntry);
		});

		$(this.timeAccountInitializationDialog).on(TimeAccountInitializationDialog.EVENT_INITIALIZED, () => {
			this.reloadCurrentTimeAccountData(true);
			this.reloadCurrentUser();
			this.reloadUsersWithInitializedTimeAccount(this.groupDropdown.getSelectedId(), this.getShowInactiveValue());
			// set by hand because the user is not reloaded (no selection when the "all users" filter is active)
			const initializedUser = this.userCombobox.getSelectedItem();
			if (initializedUser) {
				initializedUser.timeAccountState = TimeAccountState.INITIALIZED;
			}
			this.reloadActionButtons();
		});
		$(this.timeAccountTable)
			.on(TimeAccountTable.EVENT_DELETED_TIME_ACCOUNT, (e, deletedEntryType: TimeAccountEntryType) => {
				if (deletedEntryType === TimeAccountEntryType.Exit) {
					this.updateTimeAccountState(TimeAccountState.INITIALIZED);
				} else if (deletedEntryType === TimeAccountEntryType.ReEntry) {
					this.updateTimeAccountState(TimeAccountState.EXIT);
				}
			});

		// Init menu buttons
		this.reloadActionButtons();

		// Execute initial action
		const initialAction = TimeAccountDashboardQueryParams.getAnchor();

		switch (initialAction) {
			case "initializeTimeAccount":
				$("#initTimeAccountButton").click();
				TimeAccountDashboardQueryParams.clearAnchor();
				break;
			case "exitUser":
				$("#contextCreateExitBookingButton").click();
				TimeAccountDashboardQueryParams.clearAnchor();
				break;
		}
	}

	private updateTimeAccountState(timeAccountState: TimeAccountState) {
		// set by hand because the user is not reloaded
		const editedUser = this.userCombobox.getSelectedItem();
		if (editedUser) {
			editedUser.timeAccountState = timeAccountState;
		}
		this.reloadActionButtons();
	}

	protected filterUsersCombobox(userCombobox: Combobox<User>, selectedGroupId: number, showInactiveUsers: boolean = false, selectedUserId: number | null = null) {
		api("timeaccounts/filterUsers.json")
			.get({
				groupId: selectedGroupId,
				activeUsersOnly: showInactiveUsers ? false : this.getShowInactiveValue()
			})
			.done((data: { users: User[] }) => {
				if (data?.users) {
					userCombobox.updateItems(data.users);
					userCombobox.setSelectedItemById(selectedUserId ?? userCombobox.getSelectedItem()?.id, false);
				}
			});
	}

	private toggleNotInitializedWarning(showWarning: boolean) {
		$("#timeAccountNotInitializedValidation").toggle(showWarning);
		$("#initTimeAccountButton").toggle(globals.isAdmin);
	}

	private initCreateTimeAccountBalanceDialog() {
		$("#createTimeAccountBalanceDate").val(formatDate(this.getDefaultDate()));
		this.createTimeAccountBalanceDialog = new BootstrapModal.Modal({
			title: "report.timeAccountDashboard.createTimeAccountBalance",
			message: $("#createTimeAccountBalanceDialog"),
			buttons: [
				{
					text: "report.timeAccountDashboard.preview",
					action: () => this.createTimeAccountBalance(true)
						.finally(() => this.createTimeAccountBalanceDialog.resetLoadingButtons()),
					css: "btn-primary"
				},
				{
					text: "dialog.balance",
					action: () => this.createTimeAccountBalance(false)
						.finally(() => this.createTimeAccountBalanceDialog.resetLoadingButtons()),
					css: "btn-default"
				},
				{
					text: "dialog.cancel",
					css: "btn-default"
				}
			]
		});
		this.createTimeAccountBalanceUserCombobox = new Combobox<User>({
			items: this.usersWithInitializedTimeAccount,
			formatter: user => {
				return user.fullname;
			},
			idElement: $("#createTimeAccountBalanceUserId"),
			filterElement: $("#createTimeAccountBalanceUserFilter"),
			caretElement: $("#createTimeAccountBalanceUserCaret"),
			emptyMessage: messages["user.filter.all"],
			clearEntryMessage: messages["user.filter.all"]
		});
		this.createTimeAccountBalanceUserGroupDropdown = new GroupDropdown({
			rootElement: $("#createTimeAccountBalanceFilterGroupDropdown"),
			allowSelectionOfNotAssigned: false,
			emptyMessage: messages["group.filter.all"],
			clearEntryMessage: messages["group.filter.all"]
		});
		$(this.createTimeAccountBalanceUserGroupDropdown).on(GroupDropdown.EVENT_SELECTED, (e, group: Group) => {
			this.reloadUsersWithInitializedTimeAccount(group?.id ?? null, true).done(() => {
				this.createTimeAccountBalanceUserCombobox.updateItems(this.usersWithInitializedTimeAccount);
			});
		});
	}

	private initCorrectVacationBalanceDialog() {
		this.correctVacationBalanceDialog = new BootstrapModal.Modal({
			title: "report.timeAccountDashboard.correctVacationBalance",
			message: $("#correctVacationBalanceDialog"),
			buttons: [
				{
					text: "dialog.save",
					action: () => this.correctVacationBalance(),
					css: "saveButton btn-primary"
				}, {
					text: "dialog.saveChanges",
					action: () => this.editVacationBalanceCorrection(),
					css: "saveChangesButton btn-primary",
					visible: false
				}, {
					text: "dialog.cancel"
				}
			]
		});
		this.correctVacationBalanceUserCombobox = new Combobox<User>({
			items: this.usersWithInitializedTimeAccount,
			formatter: user => {
				return user.fullname;
			},
			idElement: $("#correctVacationBalanceUserId"),
			filterElement: $("#correctVacationBalanceUserFilter"),
			caretElement: $("#correctVacationBalanceUserCaret"),
			clearable: false
		});
		$(this.correctVacationBalanceUserCombobox).on(Combobox.EVENT_CHANGED, e => {
			this.configureCorrectVacationBalanceUser();
		});
		$("#correctVacationBalanceDate").val(formatDate(Temporal.Now.plainDateISO()));
		$("#correctVacationBalanceNewVacation").change(() => {
			this.setVacationBalanceNewVacation(this.getVacationBalanceNewVacation());
		});
	}

	private initExportTimeAccountDialog() {
		this.exportTimeAccountDialog = new BootstrapModal.Modal({
			title: "report.timeAccountDashboard.export",
			message: $("#exportTimeAccountDialog"),
			size: "modal-lg",
			buttons: [
				{
					text: "dialog.export",
					action: () => this.export(),
					css: "btn-primary exportButton"
				}, {
					text: "dialog.cancel"
				}
			],
			onOpen: () => {
				this.updateExportTimeAccountDialog(this.exportBalances);
			}
		});
		$("#exportTimeAccountFileFormatExcel").click(() => this.updateTimeAccountFileFormatButtons(true));
		$("#exportTimeAccountFileFormatPdf").click(() => this.updateTimeAccountFileFormatButtons(false));
		$("#exportTimeAccountBalancesBtn").click(() => this.updateExportTimeAccountDialog(true));
		$("#exportTimeAccountPayoffsBtn").click(() => this.updateExportTimeAccountDialog(false));
		$("#exportTimeAccountPayoffsFileFormatExcel").click(() => this.updateTimeAccountPayoffsFileFormatButtons(true));
		$("#exportTimeAccountPayoffsFileFormatPdf").click(() => this.updateTimeAccountPayoffsFileFormatButtons(false));

		$("#exportTimeAccountTimeSpan").on("change", e => {
			const selectTimeSpanActive = $(e.target).is(":checked");
			$("#exportTimeAccountDateToContainer").toggle(selectTimeSpanActive);
			$("#exportTimeAccountDateFromLabel").text(selectTimeSpanActive ? messages["report.timeAccountDashboard.date.from"] : messages["report.timeAccountDashboard.date"]);
			this.updateTimeAccountFileFormatButtons(this.exportExcel);
			this.reloadTimeAccountDateToSelect();
		});

		$("#exportTimeAccountDateFrom").on("change", () => this.reloadTimeAccountDateToSelect());
		$("[name=exportTimeAccountType]").on("change", () => this.displayExportTypeInputGroups());

		this.reloadTimeAccountBalanceDates();
		this.reloadTimeAccountPayoffsMonths();
		this.displayExportTypeInputGroups();

		this.exportTimeAccountBalanceUserCombobox = new Combobox<User>({
			items: this.usersWithInitializedTimeAccount,
			formatter: user => {
				return user.fullname;
			},
			idElement: $("#exportTimeAccountBalanceUserId"),
			filterElement: $("#exportTimeAccountBalanceUserFilter"),
			caretElement: $("#exportTimeAccountBalanceUserCaret"),
			emptyMessage: messages["user.filter.all"],
			clearEntryMessage: messages["user.filter.all"]
		});
		$(this.exportTimeAccountBalanceUserCombobox).on(Combobox.EVENT_CHANGED, () => this.reloadTimeAccountBalanceDates());

		this.exportTimeAccountBalanceGroupDropdown = new GroupDropdown({
			rootElement: $("#exportTimeAccountBalanceFilterGroupDropdown"),
			allowSelectionOfNotAssigned: false,
			emptyMessage: messages["group.filter.all"],
			clearEntryMessage: messages["group.filter.all"]
		});
		$(this.exportTimeAccountBalanceGroupDropdown).on(GroupDropdown.EVENT_SELECTED, () => this.reloadTimeAccountBalanceDates());
	}

	private initCreateExitBookingDialog() {
		const resignUserDate = $("#resignUserDate");
		const timeAccountCheckbox = $("#payoffTimeAccountCheckbox");
		const vacationCheckbox = $("#payoffVacationCheckbox");
		const overtimeAllowanceCheckbox = $("#payoffOvertimeAllowancesCheckbox");
		const travellingTimesCheckbox = $("#payoffTravellingTimesCheckbox");
		const description = $("#createExitBookingDescription");
		const payoffDescription = $("#createExitBookingPayoffDescription");
		const form = $("form#createExitBookingDialogForm");

		// Listeners
		resignUserDate.change(() => this.prepareCreateExitBookingDialog(false));
		timeAccountCheckbox.change(() => this.setCreateExitBookingDialogNotes(timeAccountCheckbox, vacationCheckbox, overtimeAllowanceCheckbox, travellingTimesCheckbox));
		vacationCheckbox.change(() => this.setCreateExitBookingDialogNotes(timeAccountCheckbox, vacationCheckbox, overtimeAllowanceCheckbox, travellingTimesCheckbox));
		overtimeAllowanceCheckbox.change(() => this.setCreateExitBookingDialogNotes(timeAccountCheckbox, vacationCheckbox, overtimeAllowanceCheckbox, travellingTimesCheckbox));
		travellingTimesCheckbox.change(() => this.setCreateExitBookingDialogNotes(timeAccountCheckbox, vacationCheckbox, overtimeAllowanceCheckbox, travellingTimesCheckbox));

		this.createExitBookingDialog = new BootstrapModal.Modal({
			title: "report.timeAccountDashboard.exit.book",
			size: "modal-lg",
			message: $("#createExitBookingDialog"),
			buttons: [
				{
					text: "report.timeAccountDashboard.exit.preview",
					action: () => this.createOrPreviewExitTimeSheet(true, form),
					css: "btn-default"
				}, {
					text: "report.timeAccountDashboard.exit.book",
					action: () => this.createOrPreviewExitTimeSheet(false, form),
					css: "btn-primary"
				}, {
					text: "dialog.cancel"
				}
			],
			onOpen: () => {
				timeAccountCheckbox.prop("checked", false);
				vacationCheckbox.prop("checked", false);
				overtimeAllowanceCheckbox.prop("checked", false);
				travellingTimesCheckbox.prop("checked", false);
				resignUserDate.val(formatDate(Temporal.Now.plainDateISO()));
				description.val("");
				payoffDescription.val("");

				this.prepareCreateExitBookingDialog(true);
			}
		});
	}

	private initUserChangeConfigurationDialog() {
		// Only initialize if the context menu is available
		if ($("#contextChangeConfigurationButtonContainer").length > 0) {
			this.userChangeConfigurationDialogHandler = new UserChangeConfigurationDialogHandler();
			$(this.userChangeConfigurationDialogHandler).on(UserChangeConfigurationDialogHandler.EVENT_SAVED_CONFIGURATION, () => {
				this.reloadCurrentUser();
				this.reloadUsersWithInitializedTimeAccount(this.groupDropdown.getSelectedId(), this.getShowInactiveValue());
				this.reloadCurrentTimeAccountData();
				this.validateWorkingTimes(this.groupDropdown.getSelectedId(), this.userCombobox.getSelectedItem()?.id, false);
			});

			this.loadWorkScheduleModels();
			this.loadHolidayCalendars();
		}
	}

	private updateExportTimeAccountDialog(exportBalances: boolean) {
		const balancesInfo = $("#exportTimeAccountBalancesInfo");
		const balancesWarning = $("#exportTimeAccountNoBalancesWarning");
		const exportButton = this.exportTimeAccountDialog.getButtons("exportButton");

		$("#exportTimeAccountDateToContainer").toggle($("#exportTimeAccountTimeSpan").is(":checked"));

		this.exportBalances = exportBalances;
		$("#balancesView").toggle(this.exportBalances);
		$("#exportTimeAccountBalancesBtn").toggleClass("btn-primary", this.exportBalances);
		$("#payoffsView").toggle(!this.exportBalances);
		$("#exportTimeAccountPayoffsBtn").toggleClass("btn-primary", !this.exportBalances);
		exportButton.removeProp("disabled");
		exportButton.prop("disabled", this.exportBalances ? !this.exportDatesAvailable() : !this.exportMonthsAvailable());
		if (this.exportBalances) {
			balancesInfo.text(messages["report.timeAccountDashboard.exportDescription"]);
			balancesWarning.text(messages["report.timeAccountDashboard.noBalances.error.message"]);
			balancesInfo.toggle(this.exportDatesAvailable());
			balancesWarning.toggle(!this.exportDatesAvailable());
		} else {
			balancesInfo.text(this.exportPayoffsExcel ? messages["report.timeAccountDashboard.exportDescription.payoffs.excel"] : messages["report.timeAccountDashboard.exportDescription.payoffs.pdf"]);
			balancesWarning.text(messages["report.timeAccountDashboard.noPayoffs.error.message"]);
			balancesInfo.toggle(this.exportMonthsAvailable());
			balancesWarning.toggle(!this.exportMonthsAvailable());
		}
	}

	private updateTimeAccountPayoffsFileFormatButtons(exportExcel: boolean) {
		const excelButton = $("#exportTimeAccountPayoffsFileFormatExcel");
		const pdfButton = $("#exportTimeAccountPayoffsFileFormatPdf");
		const balancesInfo = $("#exportTimeAccountBalancesInfo");

		if (this.exportPayoffsExcel !== exportExcel) {
			this.exportPayoffsExcel = exportExcel;
			excelButton.toggleClass("btn-primary", this.exportPayoffsExcel);
			pdfButton.toggleClass("btn-primary", !this.exportPayoffsExcel);
			balancesInfo.text(this.exportPayoffsExcel ? messages["report.timeAccountDashboard.exportDescription.payoffs.excel"] : messages["report.timeAccountDashboard.exportDescription.payoffs.pdf"]);
		}
	}

	private updateTimeAccountFileFormatButtons(exportExcel: boolean) {
		const excelButton = $("#exportTimeAccountFileFormatExcel");
		const pdfButton = $("#exportTimeAccountFileFormatPdf");
		const durationUnitSelect = $("#exportTimeAccountDurationFormatSelect");
		const balancesInfo = $("#exportTimeAccountBalancesInfo");

		if (this.exportExcel !== exportExcel) {
			this.exportExcel = exportExcel;
			excelButton.toggleClass("btn-primary", this.exportExcel);
			durationUnitSelect.toggle(this.exportExcel);
			pdfButton.toggleClass("btn-primary", !this.exportExcel);
		}

		// Update description
		const fileFormat = this.exportExcel ? ExportType.Excel : ExportType.Pdf;
		const dateSpan: string = $("#exportTimeAccountTimeSpan").is(":checked") ? ".dateSpan" : "";
		balancesInfo.html(messages[`report.timeAccountDashboard.exportDescription${dateSpan}.${fileFormat.toLowerCase()}`]);
	}

	private reloadTimeAccountBalanceDates() {
		return api("timeaccounts/getTimeAccountBalanceDates.json")
			.get({
				userId: this.exportTimeAccountGetUserId(),
				groupId: this.exportTimeAccountGetGroupId()
			}).done((data: { balanceDates: string[] }) => {
				this.balanceDates = data.balanceDates
					.map(date => parseISODate(date))
					.filter((date): date is Temporal.PlainDate => date !== null);
				const fromDateSelect = $("#exportTimeAccountDateFrom");
				const selected = fromDateSelect.val();
				fromDateSelect.empty();
				$.each(this.balanceDates, function (index, date) {
					const formattedDate = formatDate(date);
					fromDateSelect.append($("<option></option>", {value: formattedDate}).text(formattedDate));
					if (formattedDate === selected) {
						// Keep old value if it's available
						fromDateSelect.val(selected);
					}
				});
				this.reloadTimeAccountDateToSelect();
				this.updateExportTimeAccountDialog(this.exportBalances);
			});
	}

	private reloadTimeAccountPayoffsMonths() {
		return api("timeaccounts/getTimeAccountPayoffMonths.json")
			.get()
			.done((data: { payoffMonths: string[] }) => {
				const monthSelect = $("#exportPayoffsMonth");
				monthSelect.empty();
				$.each(data.payoffMonths, function (index, date) {
					const formattedMonth = formatMonth(parseISODate(date));
					const formattedDate = formatDate(parseISODate(date));
					monthSelect.append($("<option></option>", {value: formattedDate}).text(formattedMonth));
				});
			});
	}

	private getVacationBalanceNewVacation(): number {
		const vacationUnit = this.getSelectedVacationUnit(this.correctVacationBalanceUserCombobox);
		return parseVacation($("#correctVacationBalanceNewVacation").val() as string, vacationUnit);
	}

	private setVacationBalanceNewVacation(newVacation: number) {
		const vacationUnit = this.getSelectedVacationUnit(this.correctVacationBalanceUserCombobox);
		$("#correctVacationBalanceNewVacation").val(formatVacation(newVacation, vacationUnit, false));
	}

	private showCreateTimeAccountBalanceDialog() {
		$("#createTimeAccountBalanceDescription").val("");
		this.setDefaultUser(this.createTimeAccountBalanceUserCombobox);
		this.createTimeAccountBalanceDialog.show();
	}

	private showCorrectVacationBalanceDialog() {
		this.setDefaultUser(this.correctVacationBalanceUserCombobox);
		this.configureCorrectVacationBalanceUser();
		this.setVacationBalanceNewVacation(0);
		this.correctVacationBalanceDialog.show();
	}

	private exportDatesAvailable(): boolean {
		return $("#exportTimeAccountDateFrom").children("option").length > 0;
	}

	private exportMonthsAvailable(): boolean {
		return $("#exportPayoffsMonth").children("option").length > 0;
	}

	private showExportTimeAccountDialog() {
		this.updateExportTimeAccountDialog(this.exportBalances);
		this.exportTimeAccountDialog.show();
	}

	private showExportTimeAccountIntegrationDialog(integration: IntegrationDetails) {
		const dialog = new TimeAccountIntegrationExportDialog({
			onExport: () => this.exportTimeAccountIntegration(dialog, integration),
			exportBalances: this.balanceDates,
			integrationType: integration.type
		});
		dialog.show();
	}

	private setDefaultUser(userCombobox: Combobox<User>) {
		const defaultUser = this.userCombobox.getSelectedItem();
		userCombobox.setSelectedItem(defaultUser, true);
	}

	private getDefaultDate(): Temporal.PlainDate {
		// Last day of the previous month = first day of this month minus 1 day
		return Temporal.Now.plainDateISO().with({day: 1}).subtract({days: 1});
	}

	/**
	 * Returns the userId of the selected user in the combobox for a time sheet preview
	 * @protected
	 * @returns {number} userId
	 */
	protected getPreviewTimeSheetUserId(): number {
		return this.userCombobox.getSelectedItem()?.id;
	}

	private createTimeAccountBalance(preview: boolean): Promise<boolean> {
		const date = parseDateInput($("#createTimeAccountBalanceDate").val() as string);
		return this.createOrPreviewTimeSheet(preview, $("form#createTimeAccountBalanceDialogForm"), date);
	}

	/**
	 * Creates a time account balance or a preview of the time sheet
	 * In both cases a validation and download starts to download the time sheet
	 *
	 * @param preview
	 * @param form
	 * @param date
	 * @private
	 */
	private createOrPreviewTimeSheet(preview: boolean, form: JQuery, date: Temporal.PlainDate | null): Promise<boolean> {
		const selectedGroup = this.createTimeAccountBalanceUserGroupDropdown.getSelectedData();
		if (selectedGroup && this.memberCountForCurrentGroupSelection === 0) {
			BootstrapModal.error({
				title: "report.timeAccountDashboard.emptyGroup.title",
				message: messages.withArguments("report.timeAccountDashboard.emptyGroup.message", selectedGroup.name)
			});
			return Promise.resolve(false);
		} else if (this.usersWithInitializedTimeAccount.length > 0) {
			const formData: RequestParam[] = form.serializeArray();
			const userId = this.getTimeAccountBalanceUserId();
			const groupId = selectedGroup?.id;
			const description = $("#createTimeAccountBalanceDescription").val() as string;

			formData.push({name: "date", value: getISODate(date)});
			formData.push({name: "timeZone", value: getCurrentISOOffset()});
			formData.push({name: "userId", value: userId});
			formData.push({name: "groupId", value: groupId});
			formData.push({name: "description", value: description});
			formData.push({name: "exit", value: false});

			return this.createBookingWithClosedConfirmation(
				"timeaccounts/createTimeAccountBalance.json",
				this.createTimeAccountBalanceDialog,
				formData,
				preview,
				() => void this.previewTimeSheet(groupId, userId, date, description)
			);
		} else {
			BootstrapModal.error({
				title: "report.timeAccountDashboard.noUsersInitialized.title",
				message: "report.timeAccountDashboard.noUsersInitialized.message"
			});
			return Promise.resolve(false);
		}
	}

	/**
	 * Creates a time account exit or a preview of the time sheet
	 * In both cases a validation and download starts to download the time sheet
	 *
	 * @param preview
	 * @param form
	 * @private
	 */
	private async createOrPreviewExitTimeSheet(preview: boolean, form: JQuery) {
		if (this.usersWithInitializedTimeAccount.length > 0) {
			const formData: RequestParam[] = form.serializeArray();
			const userId = this.userCombobox.getSelectedItem()?.id;

			const resignUserDate = $("#resignUserDate");
			const resignDate = parseDateInput(resignUserDate.val() as string);
			formData.push({name: "date", value: getISODate(resignDate)});
			formData.push({name: "resignDate", value: getISODate(resignDate)});
			formData.push({name: "timeZone", value: getCurrentISOOffset()});
			formData.push({name: "userId", value: userId});
			formData.push({name: "description", value: $("#createExitBookingDescription").val() as string});
			formData.push({name: "payoffDescription", value: $("#createExitBookingPayoffDescription").val() as string});
			formData.push({name: "payoffTimeAccount", value: `${$("#payoffTimeAccountCheckbox").is(":checked")}`});
			formData.push({name: "payoffVacation", value: `${$("#payoffVacationCheckbox").is(":checked")}`});
			formData.push({name: "payoffOvertimeAllowances", value: `${$("#payoffOvertimeAllowancesCheckbox").is(":checked")}`});
			formData.push({name: "payoffTravellingTimes", value: `${$("#payoffTravellingTimesCheckbox").is(":checked")}`});
			formData.push({name: "exit", value: `${true}`});

			const success = await this.createBookingWithClosedConfirmation("timeaccounts/createTimeAccountExit.json",
				this.createExitBookingDialog,
				formData,
				preview,
				() => void this.previewExitTimeSheet(formData)
			).finally(() => this.createExitBookingDialog.resetLoadingButtons());
			if (success && !preview) {
				// set by hand because the user is not reloaded
				this.userCombobox.getSelectedItem().timeAccountState = TimeAccountState.EXIT;
				this.reloadActionButtons();
			}
		} else {
			BootstrapModal.error({
				title: "report.timeAccountDashboard.noUsersInitialized.title",
				message: "report.timeAccountDashboard.noUsersInitialized.message"
			});
		}
	}

	private async createBookingWithClosedConfirmation(url: string,
	                                                  dialog: BootstrapModal.Modal,
	                                                  formData: RequestParam[],
	                                                  preview: boolean,
	                                                  previewFunction: () => void): Promise<boolean> {
		const deferred = $.Deferred();
		if (formData.some(e => e.name === "resignDate")) {
			const hasNoFutureWorkingTimes = await this.validateNoFutureWorkingTimesAfterResignDate(formData);
			if (!hasNoFutureWorkingTimes) {
				return promiseNow(false);
			}
		}

		const createOrPreview = async (confirm: boolean) => {
			// check if this is an exit booking or a single user balance, if not show dialog
			if (!formData.some(e => e.name === "resignDate")) {
				const data: GetResignedUsersForBalanceResponse = await api("timeaccounts/getResignedUsersForBalance.json")
					.get({
						dueDate: getISODate(parseDateInput($("#createTimeAccountBalanceDate").val() as string)),
						userId: this.createTimeAccountBalanceUserCombobox.getSelectedItem()?.id,
						groupId: this.createTimeAccountBalanceUserGroupDropdown.getSelectedId()
					});

				if (data?.users?.length > 0 && !await this.showResignedUsersWithoutExitBookingDialog(data.users, preview)) {
					return;
				}
			}

			if (preview) {
				previewFunction();
				deferred.resolve(true);
			} else {
				createBookingWithClosedConfirmation(confirm);
			}
		};

		const createBookingWithClosedConfirmation = (confirm: boolean) => {
			if (confirm) {
				BootstrapModal.confirm({
					title: messages["report.timeAccountDashboard.validation.title"],
					message: $("<div>").append($("<div>").addClass("recording-validation-container").text(messages["validationResult.confirmClose"])),
					escapeText: false,
					yesLabel: messages["dialog.balance.confirmed"],
					noLabel: "confirmation.cancel",
					size: "modal-lg",
					yesAction: (modal: BootstrapModal.Modal) => {
						void api(url)
							.post(formData)
							.done((data: TimeAccountBalanceBookingResponse) => {
								dialog.close();
								$.when(
									this.timeAccountTable.reload(),
									this.reloadCurrentTimeAccountData(),
									this.reloadCurrentUser()
								).done(() => {
									this.downloadTimeAccountBalances(data.timeAccountIds, this.createTimeAccountBalanceUserGroupDropdown.getSelectedId());
									this.reloadTimeAccountBalanceDates();
									this.reloadTimeAccountPayoffsMonths();
									deferred.resolve(true);
									modal.close();
								});
							});
					},
					noAction: modal => {
						deferred.resolve(false);
						modal.close();
					}
				});
			} else {
				void api(url)
					.post(formData)
					.done((data: TimeAccountBalanceBookingResponse) => {
						dialog.close();
						$.when(
							this.timeAccountTable.reload(),
							this.reloadCurrentTimeAccountData(),
							this.reloadCurrentUser()
						).done(() => {
							this.downloadTimeAccountBalances(data.timeAccountIds, this.createTimeAccountBalanceUserGroupDropdown.getSelectedId());
							this.reloadTimeAccountBalanceDates();
							this.reloadTimeAccountPayoffsMonths();
							deferred.resolve(true);
						}).fail(() => void deferred.resolve(false));
					});
			}
		};

		// An Exit booking is only a final balance so no different validation is needed
		void api("timeaccounts/mandatoryValidationsForTimeAccountBalance.json")
			.get(formData)
			.done(mandatoryValidationsResult => {
				if (mandatoryValidationsResult.valid) {
					void api("timeaccounts/validateTimeAccountBalance.json")
						.get(formData)
						.done(validationResult => {
							if (validationResult.valid) {
								if (validationResult.balancesCount === 0) {
									BootstrapModal.info({
										title: "report.timeAccountDashboard.noBalances.title",
										message: "report.timeAccountDashboard.noBalances.message"
									});
									deferred.resolve(false);
								} else {
									void createOrPreview(false);
									deferred.resolve(true);
								}
							} else {
								BootstrapModal.confirm({
									title: messages["report.timeAccountDashboard.validation.title"],
									message: $("<div>").append($("<h6>").text(messages["validationResult.title"]))
										.append(buildRecordingValidationContainerContent(validationResult, globals.userId !== this.getTimeAccountBalanceUserId(), false)),
									escapeText: false,
									yesLabel: preview ? messages["report.timeAccountDashboard.preview"] : messages["dialog.balance.confirmed"],
									noLabel: "confirmation.cancel",
									size: "modal-lg",
									yesAction: (modal: BootstrapModal.Modal) => {
										void createOrPreview(true);
										deferred.resolve(true);
										modal.close();
									},
									noAction: (modal: BootstrapModal.Modal) => {
										deferred.resolve(false);
										modal.close();
									}
								});
							}
						})
						.fail(() => void deferred.resolve(false));
				} else {
					BootstrapModal.error({
						title: messages["report.timeAccountDashboard.validation.title"],
						message: $("<div>").append($("<h6>").text(messages["mandatoryValidationsResult.title"]))
							.append(buildRecordingValidationContainerContent(mandatoryValidationsResult, globals.userId !== this.getTimeAccountBalanceUserId(), false)),
						escapeText: false,
						size: "modal-lg"
					});
					deferred.resolve(false);
				}
			})
			.fail(() => void deferred.resolve(false));
		return deferred.promise() as JQueryPromise<boolean>;
	}

	private async validateNoFutureWorkingTimesAfterResignDate(formData: RequestParam[]): Promise<boolean> {
		const userId = Number(formData.find(e => e.name === "userId")?.value);
		const resignDate = formData.find(e => e.name === "resignDate")?.value as string;
		if (!userId || !resignDate) {
			return true;
		}

		const futureWorkingTimes = await this.loadWorkingTimeRangesAfterResignDate(userId, parseISODate(resignDate));
		if (!futureWorkingTimes || futureWorkingTimes.length === 0) {
			return true;
		}
		const warningList = $("<ul>").addClass("mb-0");
		futureWorkingTimes.forEach(timeRange => {
			const startDate = formatDate(parseISODate(timeRange.startDate));
			const endDate = formatDate(parseISODate(timeRange.endDate));
			const hasTimeRange = !!timeRange.startTime && !!timeRange.endTime;
			const startTime = hasTimeRange ? parseTimeInput(timeRange.startTime) : null;
			const endTime = hasTimeRange ? parseTimeInput(timeRange.endTime) : null;
			let dateRangeText: string;

			if (hasTimeRange && startDate === endDate) {
				dateRangeText = `${startDate} ${formatTime(startTime)} - ${formatTime(endTime)}`;
			} else if (hasTimeRange) {
				dateRangeText = `${startDate} ${formatTime(startTime)} - ${endDate} ${formatTime(endTime)}`;
			} else {
				dateRangeText = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
			}

			const warningEntry = $("<li>").append(
				$("<div>").addClass("time-account-future-working-times-row")
					.append($("<span>").addClass("date").text(dateRangeText))
					.append($("<span>").addClass("type").text(timeRange.workingTimeTypeName))
			);
			warningList.append(warningEntry);
		});
		BootstrapModal.error({
			title: messages["report.timeAccountDashboard.exit.futureWorkingTimes.title"],
			message: $("<div>")
				.append($("<div>").text(messages["report.timeAccountDashboard.exit.futureWorkingTimes.description"]))
				.append(warningList),
			escapeText: false,
			size: "modal-lg",
			buttons: [{
				text: "report.timeAccountDashboard.exit.futureWorkingTimes.showRecordings",
				action: () => {
					window.location.href = this.buildFutureWorkingTimesReportLink(userId, parseISODate(resignDate), futureWorkingTimes);
				},
				css: "btn-primary"
			}, {
				text: "confirmation.ok",
				css: "btn-default"
			}]
		});
		return false;
	}

	private buildFutureWorkingTimesReportLink(userId: number, resignDate: Temporal.PlainDate, futureWorkingTimes: WorkingTimeRange[]): string {
		const dateTo = futureWorkingTimes
			.map(timeRange => parseISODate(timeRange.endDate) ?? parseISODate(timeRange.startDate))
			.filter(date => date != null)
			.reduce((latestDate, date) => latestDate == null || Temporal.PlainDate.compare(date, latestDate) > 0 ? date : latestDate, null as Temporal.PlainDate | null);

		return ReportingQueryParams.buildLink({
			dateFrom: resignDate,
			dateTo: dateTo ?? resignDate,
			timeNavigationType: TimeNavigationType.Custom,
			userId: userId
		}, "reports/workingTime.html");
	}

	private downloadTimeAccountBalances(timeAccountIds: number[], groupId: number) {
		return api("timeaccounts/downloadTimeSheet.json")
			.get({
				ids: timeAccountIds,
				groupId: groupId,
				timeZone: getCurrentISOOffset()
			})
			.done(data => {
				if (data.exportJobId) {
					new DownloadStatus({
						exportJobId: data.exportJobId,
						exportType: ExportType.Pdf,
						allowCancellation: true,
						loadingMessage: messages["downloadStatus.loadingMessage.timeAccountBalance"]
					});
				}
			});
	}

	private correctVacationBalance() {
		if (this.usersWithInitializedTimeAccount.length > 0) {
			const selectedUser = this.correctVacationBalanceUserCombobox.getSelectedItem();
			api("timeaccounts/correctVacationBalance.json")
				.post({
					userId: selectedUser != null ? selectedUser.id : null,
					date: getISODate(parseDateInput($("#correctVacationBalanceDate").val() as string)),
					newVacation: this.getVacationBalanceNewVacation(),
					description: $("#correctVacationBalanceDescription").val() as string
				})
				.done(() => {
					this.timeAccountTable.reload();
					this.reloadCurrentTimeAccountData();
					this.correctVacationBalanceDialog.close();
				})
				.always(() => this.correctVacationBalanceDialog.resetLoadingButtons());
		} else {
			BootstrapModal.error({
				title: "report.timeAccountDashboard.noUsersInitialized.title",
				message: "report.timeAccountDashboard.noUsersInitialized.message"
			});
		}
	}

	private editVacationBalanceCorrection() {
		if (this.usersWithInitializedTimeAccount.length > 0) {
			const selectedUser = this.correctVacationBalanceUserCombobox.getSelectedItem();
			api("timeaccounts/editVacationBalanceCorrection.json")
				.post({
					timeAccountId: $("#correctVacationBalanceTimeAccountId").val() as number,
					userId: selectedUser != null ? selectedUser.id : null,
					date: getISODate(parseDateInput($("#correctVacationBalanceDate").val() as string)),
					newVacation: this.getVacationBalanceNewVacation(),
					description: $("#correctVacationBalanceDescription").val() as string
				})
				.done(() => {
					this.timeAccountTable.reload();
					this.reloadCurrentTimeAccountData();
					this.correctVacationBalanceDialog.close();
				})
				.always(() => this.correctVacationBalanceDialog.resetLoadingButtons());
		} else {
			BootstrapModal.error({
				title: "report.timeAccountDashboard.noUsersInitialized.title",
				message: "report.timeAccountDashboard.noUsersInitialized.message"
			});
		}
	}

	private export() {
		if (this.exportBalances) {
			if (this.exportExcel) {
				this.exportTimeAccount();
			} else {
				this.exportTimeSheet();
			}
		} else {
			this.exportPayoffs();
		}
	}

	private initTimeAccountIntegrationExport() {
		$(".exportTimeAccountIntegrationButtons").on("click", (e) => {
			const integrationId = Number((e.currentTarget).dataset.integrationId);
			const integration = (this.config.timeAccountExportIntegrations
				.find(timeAccountExportIntegration => timeAccountExportIntegration.id === integrationId) as TimeAccountExportIntegrationDetails | undefined);
			if (!integration) {
				return;
			}
			if (integration.configuration
				&& !integration.configuration.timeAccountAbsenceExportEnabled
				&& !integration.configuration.timeAccountPayrollExportEnabled) {
				BootstrapModal.error({
					message: "report.timeAccountDashboard.export.integration.notEnabled"
				});
				return;
			}
			this.showExportTimeAccountIntegrationDialog(integration);
		});
	}

	private exportTimeAccountIntegration(dialog: TimeAccountIntegrationExportDialog, integration: TimeAccountExportIntegrationDetails) {
		const selectedDate = parseDateInput(dialog.getSelectedDate());
		void api("export/timeAccountIntegrations.json")
			.post({
				integrationId: integration.id,
				date: getISODate(selectedDate)
			})
			.done(data => {
				if (data.exportJobId) {
					new DownloadStatus({
						exportJobId: data.exportJobId,
						exportType: ExportType.Csv,
						allowCancellation: true,
						loadingMessage: messages["downloadStatus.loadingMessage.reports.csv"]
					});
				}
				dialog.close();
			})
			.fail((response: ErrorResponse) => {
				const fieldErrors = response.responseJSON?.fieldErrors;
				if (fieldErrors?.length) {
					dialog.showError(fieldErrors[0].message);
				}
				dialog.resetLoadingButtons();
			});
	}

	private exportTimeAccount() {
		const dateFrom = parseDateInput($("#exportTimeAccountDateFrom").val() as string);
		const durationFormat = $("#exportTimeAccountDurationFormat").val() as string;
		if (dateFrom && durationFormat) {
			api("export/timeAccount.json")
				.post({
					dateFrom: getISODate(dateFrom),
					dateTo: getISODate(this.getDateToForExports()),
					durationFormat: durationFormat,
					userId: this.exportTimeAccountGetUserId(),
					groupId: this.exportTimeAccountGetGroupId()
				})
				.done(data => {
					if (data.exportJobId) {
						new DownloadStatus({
							exportJobId: data.exportJobId,
							exportType: ExportType.Excel,
							allowCancellation: true,
							loadingMessage: messages["downloadStatus.loadingMessage.timeAccountBalance"]
						});
					}
				});
			this.exportTimeAccountDialog.close();
		} else {
			this.exportTimeAccountDialog.resetLoadingButtons();
		}
	}

	private exportPayoffs() {
		const date = parseDateInput($("#exportPayoffsMonth").val() as string);
		const durationFormat = $("#exportPayoffDurationFormat").val() as string;
		if (date && durationFormat) {
			api("export/payoffs.json")
				.post({
					date: getISODate(date),
					durationFormat: durationFormat,
					exportFileType: this.exportPayoffsExcel ? ExportType.Excel : ExportType.Pdf
				})
				.done(data => {
					if (data.exportJobId) {
						new DownloadStatus({
							exportJobId: data.exportJobId,
							exportType: this.exportPayoffsExcel ? ExportType.Excel : ExportType.Pdf,
							allowCancellation: true,
							loadingMessage: messages["downloadStatus.loadingMessage.timeAccountBalance"]
						});
					}
				});
			this.exportTimeAccountDialog.close();
		} else {
			this.exportTimeAccountDialog.resetLoadingButtons();
		}
	}

	private exportTimeSheet() {
		const dateFrom = parseDateInput($("#exportTimeAccountDateFrom").val() as string);
		if (dateFrom) {
			api("export/timeSheet.json")
				.post({
					dateFrom: getISODate(dateFrom),
					dateTo: getISODate(this.getDateToForExports()),
					userId: this.exportTimeAccountGetUserId(),
					groupId: this.exportTimeAccountGetGroupId()
				})
				.done(data => {
					if (data.exportJobId) {
						new DownloadStatus({
							exportJobId: data.exportJobId,
							exportType: ExportType.Pdf,
							allowCancellation: true,
							loadingMessage: messages["downloadStatus.loadingMessage.timeAccountBalance"]
						});
					}
				});
			this.exportTimeAccountDialog.close();
		} else {
			this.exportTimeAccountDialog.resetLoadingButtons();
		}
	}

	/**
	 * Return the to date for the export.
	 * If no timespan is selected the from date is used to ensure just one day is exported.
	 */
	private getDateToForExports(): Temporal.PlainDate | null {
		return $("#exportTimeAccountTimeSpan").is(":checked")
			? parseDateInput($("#exportTimeAccountDateTo").val() as string)
			: parseDateInput($("#exportTimeAccountDateFrom").val() as string);
	}

	private reloadCurrentTimeAccountData(shouldAlwaysValidateWorkingTimes: boolean = false): JQueryPromise<TimeAccountTableRow[]> {
		$("#timeAccountTable_processing").show();

		// Reload contextmenu entries (active/inactive)
		this.reloadActionButtons();

		this.toggleMenuLoadingButton(false);
		this.toggleTimeVacationAccountInfoPanel(true);
		this.toggleNotInitializedWarning(false);
		this.reloadTimeAccountPayoffsMonths();
		const hasCurrentTimeAccountDataRequest = this.updateCurrentTimeAccountDataRequest != null;
		this.updateCurrentTimeAccountDataRequest?.abort();
		if (hasCurrentTimeAccountDataRequest) {
			TimeVacationAccountInfo.toggleTimeVacationAccountInfoPanelLoadingIndicator(false);
		}
		const hasValidateWorkingTimesRequest = this.updateValidateWorkingTimesRequest != null;
		this.updateValidateWorkingTimesRequest?.abort();
		if (hasValidateWorkingTimesRequest) {
			this.validationsCollapseComponent.toggleLoadingOverlay(false);
		}
		const user = this.userCombobox.getSelectedItem();
		const userId = user?.id;
		const filterByUserPromise = this.timeAccountTable.filterByGroupAndUserAndAction(this.groupDropdown.getSelectedId(), userId, this.timeAccountEntryType);
		if (shouldAlwaysValidateWorkingTimes || this.shouldValidateWorkingTimes(user)) {
			this.validateWorkingTimes(this.groupDropdown.getSelectedId(), userId, true);
		} else {
			this.validationsCollapseComponent.reset();
		}
		this.toggleUserLicenceOnExitInfo(user);
		if (user) {
			this.toggleMenuLoadingButton(false);
			this.getCurrentTimeAccountVacationData(BrowserStorageSettings.getTimeAccountDashboardBalanceMode(TimeAccountBase.GROUP_TIME_ACCOUNT) || "TODAY", userId);
		} else {
			this.showTimeAccountData(null);
		}
		return filterByUserPromise;
	}

	private toggleUserLicenceOnExitInfo(user?: User) {
		const resignDate = parseISODate(user?.resignDate);
		const $userLicenceOnExitInfo = $("#userLicenceOnExitInfo");
		const show = user?.timeAccountState === TimeAccountState.EXIT && isSameOrAfterDay(resignDate, Temporal.Now.plainDateISO());
		if (show) {
			$userLicenceOnExitInfo.find(".js-user-licence-on-exit-text")
				.text(messages.withArguments("timeAccount.userLicenceOnExitInfo", formatDate(resignDate)));
		}
		$userLicenceOnExitInfo.toggle(show);
	}

	private reloadActionButtons() {
		$("#contextChangeConfigurationButtonContainer")
			.off()
			.on("click", "#contextChangeConfigurationButton", () => {
				if (this.userChangeConfigurationDialogHandler) {
					this.userChangeConfigurationDialogHandler.setUser(this.userCombobox.getSelectedItem());
					this.userChangeConfigurationDialogHandler.setWorkScheduleModels(this.workScheduleModels);
					this.userChangeConfigurationDialogHandler.setHolidayCalendars(this.holidayCalendars);
					this.userChangeConfigurationDialogHandler.open();
				}
			})
			.toggle(this.userCombobox.getSelectedItem()?.timeAccountState === TimeAccountState.INITIALIZED);

		$("#contextCreateExitBookingButtonContainer")
			.off()
			.on("click", "#contextCreateExitBookingButton", () => {
				// time account exit for an owner must not be possible
				if (this.getSelectedUserId() === globals.ownerId) {
					BootstrapModal.error({message: "report.timeAccountDashboard.exit.owner"});
				} else {
					this.createExitBookingDialog.show();
				}
			})
			.toggle(this.userCombobox.getSelectedItem()?.timeAccountState === TimeAccountState.INITIALIZED);

		$("#contextCreateReEntryBookingButtonContainer")
			.off()
			.on("click", "#contextCreateReEntryBookingButton", () => {
				this.timeAccountInitializationDialog.showForUser(this.userCombobox.getSelectedItem(), TimeAccountEntryType.ReEntry);
			})
			.toggle(this.userCombobox.getSelectedItem()?.timeAccountState === TimeAccountState.EXIT);
	}

	protected showTimeAccountData(timeAccountData: TimeAccountVacationData) {
		super.showTimeAccountData(timeAccountData);

		const isInitialized = timeAccountData?.isInitialized || false;
		this.toggleNotInitializedWarning(this.getSelectedUserId() != null && !isInitialized);
		this.toggleTimeVacationAccountInfoPanel(timeAccountData != null && isInitialized);
	}

	protected onBalanceModeChanged(mode: TimeAccountDashboardBalanceMode) {
		BrowserStorageSettings.setTimeAccountDashboardBalanceMode(TimeAccountDashboardPage.GROUP_TIME_ACCOUNT, mode);
		this.setPreviewTimeSheetDialogDate(mode);
		const userId = this.getSelectedUserId();
		if (userId) {
			this.getCurrentTimeAccountVacationData(mode, userId);
		}
	}

	private toggleTimeVacationAccountInfoPanel(showPanel: boolean) {
		$("#timeVacationAccountInfoPanel").toggle(showPanel);
	}

	private getTimeAccountBalanceUserId(): number {
		const user = this.createTimeAccountBalanceUserCombobox.getSelectedItem();
		return user ? user.id : null;
	}

	private getSelectedUserId(): number {
		const user = this.userCombobox?.getSelectedItem();
		return user ? user.id : null;
	}

	private getSelectedVacationUnit(userCombobox: Combobox<User>): VacationUnit {
		const selectedUser = userCombobox.getSelectedItem();
		return selectedUser != null ? selectedUser.vacationUnit : null;
	}

	private configureCorrectVacationBalanceUser(timeAccount?: TimeAccountTableRow) {
		if (timeAccount) {
			this.correctVacationBalanceUserCombobox.setSelectedItemById(timeAccount.userId);
			$("#correctVacationBalanceDate").val(formatDate(parseISODate(timeAccount.date)));
		}
		this.correctVacationBalanceUserCombobox.setDisabled(!!timeAccount);
		$("#correctVacationBalanceTimeAccountId").val(timeAccount ? timeAccount.id : "");
		this.correctVacationBalanceDialog.toggleButton("saveButton", !timeAccount);
		this.correctVacationBalanceDialog.toggleButton("saveChangesButton", !!timeAccount);
		$("#correctVacationBalanceDescription").val(timeAccount?.description ?? "");
		this.setVacationBalanceNewVacation(timeAccount?.vacationNew ?? 0);
		const form = $("#correctVacationBalanceDialogForm");
		const vacationUnit = this.getSelectedVacationUnit(this.correctVacationBalanceUserCombobox);
		form.find(".vacationUnitLabel").text(TimeVacationAccountInfo.getVacationUnitLabel(vacationUnit));
	}

	private reloadUsersWithInitializedTimeAccount(groupId: number | null, activeUsersOnly: boolean): JQuery.jqXHR {
		return api("timeaccounts/getUsersForTimeAccount.json")
			.get({
				groupId: groupId,
				activeUsersOnly: activeUsersOnly
			})
			.done(data => {
				this.memberCountForCurrentGroupSelection = groupId ? data.memberCount : null;
				this.setUsersWithInitializedTimeAccount(data.usersWithInitializedTimeAccount);
			});
	}

	private getShowInactiveValue(): boolean {
		return !$("#timeAccountDashboardShowInactive").is(":checked");
	}

	private setUsersWithInitializedTimeAccount(usersWithInitializedTimeAccount: User[]) {
		this.usersWithInitializedTimeAccount = usersWithInitializedTimeAccount;
		this.correctWorkBalanceDialog.setUsersWithInitializedTimeAccount(usersWithInitializedTimeAccount);
		this.correctVacationBalanceUserCombobox.updateItems(usersWithInitializedTimeAccount);
		this.createTimeAccountBalanceUserCombobox.updateItems(usersWithInitializedTimeAccount);
	}

	private onSelectedUserChanged() {
		this.reloadCurrentTimeAccountData();
	}

	/**
	 * Decides whether working times should be validated.Returns true if the clients has to be working times validating activated
	 * and the selected user has an initialized time account and no exit booking or no user is selected (selected group, or no filter at all)
	 *
	 * @param selectedUser
	 * @private
	 */
	private shouldValidateWorkingTimes(selectedUser: User): boolean {
		return (globals.validateWorkingTime || globals.validateWorkingTimeOverlaps) &&
			(!selectedUser || selectedUser && this.usersWithInitializedTimeAccount.some(user => user.id === selectedUser.id) &&
				selectedUser.timeAccountState !== TimeAccountState.EXIT);
	}

	private prepareCreateExitBookingDialog(resetInputFields: boolean) {
		// Hide time account/overtime data if no user with initialized time account available
		if (!this.userCombobox.getSelectedItem()) {
			return;
		}

		const timeAccountTotalBalance = this.createExitBookingDialog.find("#timeAccountTotalBalance");
		const vacationTotalBalance = this.createExitBookingDialog.find("#vacationTotalBalance");
		const overtimeAllowanceTotalBalance = this.createExitBookingDialog.find("#overtimeAllowanceTotalBalance");
		const travellingTimesTotalBalance = this.createExitBookingDialog.find("#travellingTimesTotalBalance");
		if (resetInputFields) {
			timeAccountTotalBalance.val(formatDuration(0));
			vacationTotalBalance.val(formatDuration(0));
			overtimeAllowanceTotalBalance.val(formatDuration(0));
			travellingTimesTotalBalance.val(formatDuration(0));
		}
		const user = this.userCombobox.getSelectedItem();
		const date = parseDateInput(this.createExitBookingDialog.find("#resignUserDate").val() as string);
		const loadingIndicator = this.createExitBookingDialog.find(".panel-processing");
		if (user?.id) {
			if (date) {
				loadingIndicator.toggle(true);
				this.loadExitBookingBalanceData(user.id, date)
					.then((timeAccount: TimeAccountExitBookingBalanceData) => {
						loadingIndicator.toggle(false);
						const showOvertimes = timeAccount.addOvertimes === true;
						const showTravellingTimes = timeAccount.addTravellingTimes === true;
						this.createExitBookingDialog.find("#exitBookingOvertimesItem")
							.prop("hidden", !showOvertimes);
						this.createExitBookingDialog.find("#exitBookingTravellingTimesItem")
							.prop("hidden", !showTravellingTimes);

						TimeVacationAccountInfo.setDurationFieldValue(timeAccountTotalBalance, timeAccount.workDurationCurrentTotalBalance);
						TimeVacationAccountInfo.setVacationFieldValue(vacationTotalBalance, timeAccount.vacationTotalBalance, timeAccount.vacationUnit, true);
						this.createExitBookingDialog.find("#vacationUnit").text(TimeVacationAccountInfo.getVacationUnitLabel(timeAccount.vacationUnit));
						this.createExitBookingDialog.find("#vacationUnit").val(timeAccount.vacationUnit);
						TimeVacationAccountInfo.setDurationFieldValue(overtimeAllowanceTotalBalance,
							// print "-" if 0
							timeAccount.overtimeDurationCurrentTotalBalance + timeAccount.allowanceDurationCurrentTotalBalance || null
						);
						TimeVacationAccountInfo.setDurationFieldValue(travellingTimesTotalBalance,
							// print "-" if 0
							timeAccount.travellingTimesDurationCurrentTotalBalance || null
						);

						// toggle checkboxes
						Input.toggleCheckbox(this.createExitBookingDialog.find("#payoffTimeAccountCheckbox"), timeAccount.workDurationCurrentTotalBalance <= 0, true);
						Input.toggleCheckbox(this.createExitBookingDialog.find("#payoffVacationCheckbox"), !timeAccount.vacationTotalBalance || timeAccount.vacationTotalBalance <= 0, true);
						Input.toggleCheckbox(this.createExitBookingDialog.find("#payoffOvertimeAllowancesCheckbox"),
							!showOvertimes || timeAccount.overtimeDurationCurrentTotalBalance + timeAccount.allowanceDurationCurrentTotalBalance <= 0, true);
						Input.toggleCheckbox(this.createExitBookingDialog.find("#payoffTravellingTimesCheckbox"),
							!showTravellingTimes || timeAccount.travellingTimesDurationCurrentTotalBalance <= 0, true);
					});
			}
		}
	}


	private loadExitBookingBalanceData(userId: number, date: Temporal.PlainDate): PromiseLike<TimeAccountExitBookingBalanceData> {
		this.loadExitBookingBalanceDataRequest?.abort();
		this.loadExitBookingBalanceDataRequest = api("timeaccounts/getExitBookingBalanceData.json")
			.get({
				userId: userId,
				date: getISODate(date)
			});
		return this.loadExitBookingBalanceDataRequest;
	}

	private loadWorkingTimeRangesAfterResignDate(userId: number, resignDate: Temporal.PlainDate | null): PromiseLike<WorkingTimeRange[]> {
		this.loadFutureWorkingTimesAfterResignDateRequest?.abort();
		this.loadFutureWorkingTimesAfterResignDateRequest = api("timeaccounts/getWorkingTimeRangesAfterDate.json")
			.get({
				userId: userId,
				date: getISODate(resignDate)
			});
		return this.loadFutureWorkingTimesAfterResignDateRequest;
	}

	private setCreateExitBookingDialogNotes(timeAccountCheckbox: JQuery, vacationCheckbox: JQuery, overtimeAllowancesCheckbox: JQuery, travellingTimesCheckbox: JQuery) {
		const timeAccountTotalBalance = this.createExitBookingDialog.find("#timeAccountTotalBalance").val() as string;
		const vacationTotalBalance = this.createExitBookingDialog.find("#vacationTotalBalance").val() as string;
		const vacationUnit: VacationUnit = this.createExitBookingDialog.find("#vacationUnit").val() as VacationUnit;
		const overtimeAllowanceTotalBalance = this.createExitBookingDialog.find("#overtimeAllowanceTotalBalance").val() as string;
		const travellingTimesTotalBalance = this.createExitBookingDialog.find("#travellingTimesTotalBalance").val() as string;
		const descriptionTextArea = $("#createExitBookingPayoffDescription");
		let description = "";
		description += timeAccountCheckbox.is(":checked")
			? messages.withArguments("report.timeAccountDashboard.balanceWorkDuration.exit.timeAccount", formatDuration(Number(timeAccountTotalBalance))) + "\n"
			: "";
		description += vacationCheckbox.is(":checked")
			? messages.withArguments("report.timeAccountDashboard.balanceWorkDuration.exit.vacation",
			formatVacation(Number(vacationTotalBalance), vacationUnit),
			TimeVacationAccountInfo.getVacationUnitLabel(vacationUnit)
		) + "\n" : "";
		description += overtimeAllowancesCheckbox.is(":checked")
			? messages.withArguments("report.timeAccountDashboard.balanceWorkDuration.exit.overtimeAllowances", formatDuration(Number(overtimeAllowanceTotalBalance))) + "\n"
			: "";
		description += travellingTimesCheckbox.is(":checked")
			? messages.withArguments("report.timeAccountDashboard.balanceWorkDuration.exit.travellingTimes", formatDuration(Number(travellingTimesTotalBalance))) + "\n"
			: "";
		descriptionTextArea.val(description);
	}

	private showResignedUsersWithoutExitBookingDialog(users: User[], preview: boolean): JQueryPromise<boolean> {
		const deferred = $.Deferred();
		BootstrapModal.confirm(
			{
				message: $("<div>").append(
					$("<p>").html(messages["report.timeAccountDashboard.balance.resignedUserWithoutExit"]),
				).append(
					$("<table>").addClass("recording-validation-container").html(
						`${users
							.map((user: User) => `
										<td>${htmlEscape(user.fullname)}</td>
										<td>${messages["report.timeAccountDashboard.resignDate"]}: ${formatDate(parseISODate(user.resignDate))}</td>
									`)
							.reduce((prev, curr) => `<tr>${prev}</tr><tr>${curr}</tr>`)}`
					)
				),
				destroyOnClose: true,
				preventDoubleClickButtons: true,
				yesLabel: messages[`report.timeAccountDashboard.resignedUsersWithoutExitBooking.${preview ? "preview" : "confirm"}`],
				yesAction: (modal: BootstrapModal.Modal) => {
					modal.close();
					deferred.resolve(true);
				},
				noAction: (modal: BootstrapModal.Modal) => {
					modal.close();
					deferred.resolve(false);
				}
			}
		);
		return deferred.promise() as JQueryPromise<boolean>;
	}

	private reloadTimeAccountDateToSelect() {
		const fromDateSelect = $("#exportTimeAccountDateFrom");
		const toDateSelect = $("#exportTimeAccountDateTo");
		const selected = toDateSelect.val();
		toDateSelect.empty();
		$.each(this.balanceDates, function (index, date) {
			if (isSameOrBeforeDay(parseDateInput(fromDateSelect.val() as string), date)) {
				const formattedDate = formatDate(date);
				toDateSelect.append($("<option></option>", {value: formattedDate}).text(formattedDate));
				if (formattedDate === selected) {
					// Keep selected if it's available
					toDateSelect.val(selected);
				}
			}
		});
	}

	private displayExportTypeInputGroups() {
		const exportTimeAccountType: ExportTimeAccountType = $("[name=exportTimeAccountType]:checked").val() as ExportTimeAccountType;
		$("#exportTimeAccountUserFilterContainer").toggle(exportTimeAccountType === ExportTimeAccountType.USER);
		$("#exportTimeAccountBalanceFilterGroupDropdown").toggle(exportTimeAccountType === ExportTimeAccountType.GROUP);
		this.reloadTimeAccountBalanceDates();
	}

	private exportTimeAccountGetUserId() {
		const exportTimeAccountType: ExportTimeAccountType = $("[name=exportTimeAccountType]:checked").val() as ExportTimeAccountType;
		return exportTimeAccountType === ExportTimeAccountType.USER ? this.exportTimeAccountBalanceUserCombobox.getSelectedItem()?.id : null;
	}

	private exportTimeAccountGetGroupId() {
		const exportTimeAccountType: ExportTimeAccountType = $("[name=exportTimeAccountType]:checked").val() as ExportTimeAccountType;
		return exportTimeAccountType === ExportTimeAccountType.GROUP ? this.exportTimeAccountBalanceGroupDropdown.getSelectedId() : null;
	}

	private loadWorkScheduleModels() {
		api("admin/findWorkScheduleModelsForSelect.json")
			.get({
				sort: "name", dir: "asc"
			})
			.done((workScheduleModels: WorkScheduleModel[]) => {
				this.workScheduleModels = workScheduleModels;
			})
			.fail(() => this.workScheduleModels = []);
	}

	private loadHolidayCalendars() {
		if (SecurityStatus.isHolidayCalendarAllowed()) {
			api("admin/holidayCalendars.json")
				.get()
				.done((data: { holidayCalendars: HolidayCalendar[] }) => {
					this.holidayCalendars = data.holidayCalendars;
				})
				.fail(() => this.holidayCalendars = []);
		} else {
			this.holidayCalendars = [];
		}
	}

	private reloadCurrentUser() {
		if (this.userCombobox?.getSelectedItem()?.id) {
			return api("timeaccounts/getUser.json")
				.get({
					userId: this.userCombobox.getSelectedItem()?.id
				})
				.done((user: User) => {
					this.userCombobox.setSelectedItem(user);
				});
		}
	}
}
